import { useEffect, useState } from 'react';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { doc, updateDoc } from 'firebase/firestore';

import { db } from '@lib/firebase';
import { useAuth } from './useAuth';

/**
 * Hook to initialize Firebase Cloud Messaging and request permission
 */
export function useFCM(): {
  token: string | null;
  isSupported: boolean;
  requestPermission: () => Promise<void>;
} {
  const { user } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [messaging, setMessaging] = useState<Messaging | null>(null);

  useEffect(() => {
    // Check if browser supports FCM
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        const messagingInstance = getMessaging();
        setMessaging(messagingInstance);
        setIsSupported(true);
      } catch (error) {
        console.error('FCM not supported:', error);
        setIsSupported(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!messaging || !isSupported) return;

    // Request permission and get token
    const requestPermissionAndGetToken = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
          if (!vapidKey) {
            console.warn('VAPID key not configured');
            return;
          }

          const currentToken = await getToken(messaging, { vapidKey });
          if (currentToken) {
            setToken(currentToken);

            // Save token to user document
            if (user?.id) {
              await updateDoc(doc(db, 'users', user.id), {
                fcmToken: currentToken,
                fcmTokenUpdatedAt: new Date(),
              });
            }
          }
        }
      } catch (error) {
        console.error('Error getting FCM token:', error);
      }
    };

    requestPermissionAndGetToken();

    // Listen for foreground messages
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Message received:', payload);
      // You can show a notification here or update UI
      if (payload.notification) {
        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(payload.notification.title || '', {
            body: payload.notification.body,
            icon: payload.notification.icon,
          });
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [messaging, isSupported, user]);

  const requestPermission = async () => {
    if (!messaging || !isSupported) return;

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
        if (vapidKey) {
          const currentToken = await getToken(messaging, { vapidKey });
          if (currentToken) {
            setToken(currentToken);
            if (user?.id) {
              await updateDoc(doc(db, 'users', user.id), {
                fcmToken: currentToken,
                fcmTokenUpdatedAt: new Date(),
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error requesting FCM permission:', error);
    }
  };

  return { token, isSupported, requestPermission };
}

